import { useEffect, useState, type ReactNode } from 'react';
import {
  LOCALE_TAGS,
  type BookingGiftOption,
  localize,
  type Locale,
  type PaymentMethod,
  type Staff,
} from '@360face/domain';
import { ApiError, type HubApi, type KioskVisit } from '../api/client';
import { endTime } from '../i18n/format';
import { errorText, type Messages } from '../i18n/messages';

/** The two welcome languages of the location (local + English); PT/EN until the server answers. */
export function useKioskConfig(api: HubApi, locationId: string): { welcome: Locale[]; gifts: BookingGiftOption[] } {
  const [cfg, setCfg] = useState<{ welcome: Locale[]; gifts: BookingGiftOption[] }>({
    welcome: ['pt', 'en'],
    gifts: [],
  });
  useEffect(() => {
    api.kioskConfig(locationId).then(
      (c) =>
        setCfg({
          welcome: c.welcomeLanguages.length > 0 ? c.welcomeLanguages.slice(0, 2) : ['pt', 'en'],
          gifts: c.bookingGifts ?? [],
        }),
      () => undefined,
    );
  }, [api, locationId]);
  return cfg;
}

/**
 * The booking in one line (owner 15.10.2026): which treatments, how long in total, and from
 * when to when — e.g. "Express to Impress (+ Buccal Sculpt) · 55 min · 10:00–10:55".
 */
export function BookingLine({ visit, m, locale }: { visit: KioskVisit; m: Messages; locale: Locale }) {
  const main = visit.lines.filter((l) => l.kind === 'service').map((l) => l.title);
  const extras = visit.lines.filter((l) => l.kind === 'addon').map((l) => l.title);
  const start = visit.start.slice(11, 16);
  return (
    <div className="booking-line">
      <b>
        {main.join(' + ')}
        {extras.length > 0 && <small> (+ {extras.join(', ')})</small>}
      </b>
      <span>
        {visit.durationMin} {m.minutes} · {start}–{endTime(visit.start, visit.durationMin)}
      </span>
      {visit.bookingGift && (
        <span className="gift-line">
          🎁 {m.presentLabel}: {localize(visit.bookingGift.title, locale)}
        </span>
      )}
    </div>
  );
}

/** Button that runs an async action, shows progress and a translated error. */
export function SubmitButton({
  label,
  busyLabel,
  run,
  m,
  disabled,
  className = 'btn primary',
}: {
  label: ReactNode;
  busyLabel: string;
  run: () => Promise<void>;
  m: Messages;
  disabled?: boolean;
  className?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  return (
    <>
      <button
        className={className}
        disabled={busy || disabled}
        aria-busy={busy}
        onClick={async () => {
          setBusy(true);
          setError(undefined);
          try {
            await run();
          } catch (e) {
            setError(e instanceof ApiError ? e.code : 'default');
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? busyLabel : label}
      </button>
      {error && (
        <p className="err" role="alert">
          {errorText(m, error)}
        </p>
      )}
    </>
  );
}

/**
 * Renders loading, error-with-retry and success states for one request.
 * Results are tagged with the deps they were loaded for, so a reused component
 * never renders data from the previous step.
 */
export function Load<T>({
  load,
  m,
  deps,
  children,
}: {
  load: () => Promise<T>;
  m: Messages;
  deps: unknown[];
  children: (data: T) => ReactNode;
}) {
  const key = JSON.stringify(deps);
  const [state, setState] = useState<{ key?: string; data?: T; error?: string }>({});
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let alive = true;
    load().then(
      (data) => alive && setState({ key, data }),
      (e: unknown) => alive && setState({ key, error: e instanceof ApiError ? e.code : 'default' }),
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);

  if (state.key !== key)
    return (
      <div className="center">
        <div className="spinner" aria-label={m.loading} />
      </div>
    );
  if (state.error)
    return (
      <div className="card error" role="alert">
        <p>{errorText(m, state.error)}</p>
        <button
          className="btn primary"
          onClick={() => {
            setState({});
            setAttempt((a) => a + 1);
          }}
        >
          {m.retry}
        </button>
      </div>
    );
  return <>{children(state.data as T)}</>;
}

export function useStaff(api: HubApi, locationId: string): Staff[] {
  const [staff, setStaff] = useState<Staff[]>([]);
  useEffect(() => {
    api.staff(locationId).then(setStaff, () => setStaff([]));
  }, [api, locationId]);
  return staff;
}

export function weekday(isoDate: string, locale: Locale): string {
  return new Intl.DateTimeFormat(LOCALE_TAGS[locale], { weekday: 'short', timeZone: 'UTC' }).format(
    new Date(`${isoDate}T12:00:00Z`),
  );
}

export function month(isoDate: string, locale: Locale): string {
  return new Intl.DateTimeFormat(LOCALE_TAGS[locale], { month: 'short', timeZone: 'UTC' }).format(
    new Date(`${isoDate}T12:00:00Z`),
  );
}

export function CalendarIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="5" width="18" height="16" rx="3" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  );
}

export function PhoneCallIcon() {
  return (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M15.5 4.5h4v4M19.5 4.5L14 10" />
      <path d="M5 3.5h3l1.5 4-2 1.3a11 11 0 0 0 6.7 6.7l1.3-2 4 1.5v3a2 2 0 0 1-2 2A16 16 0 0 1 3 5.5a2 2 0 0 1 2-2z" />
    </svg>
  );
}

export function GlobeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.7 3.8 5.7 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-5.7-3.8-9S9.5 5.7 12 3z" />
    </svg>
  );
}

export function GiftIcon({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <rect x="3.5" y="9" width="17" height="11" rx="2.5" />
      <path d="M2.5 9h19M12 9v11M12 9c-1.5-3.5-6-4-6-1.5S10 9 12 9zm0 0c1.5-3.5 6-4 6-1.5S14 9 12 9z" />
    </svg>
  );
}

export function CheckIcon({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

export const PAY_ICON: Record<PaymentMethod, ReactNode> = {
  card_terminal: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="2.5" y="5" width="19" height="14" rx="3" />
      <path d="M2.5 10h19" />
    </svg>
  ),
  mbway: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="6" y="2.5" width="12" height="19" rx="3" />
      <path d="M10 18h4" />
    </svg>
  ),
  qr_transfer: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M14 14h3v3h-3zM20 14v.01M14 20h.01M17 20h4v-3" />
    </svg>
  ),
  cash: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="2.5" y="6" width="19" height="12" rx="3" />
      <circle cx="12" cy="12" r="2.5" />
    </svg>
  ),
  bank_transfer: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3 9l9-5 9 5M5 10v8M10 10v8M14 10v8M19 10v8M3 20h18" />
    </svg>
  ),
  gift_card: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="8" width="18" height="13" rx="2" />
      <path d="M3 12h18M12 8v13M12 8c-1.5-3-5-3.5-5-1.5S10 8 12 8zM12 8c1.5-3 5-3.5 5-1.5S14 8 12 8z" />
    </svg>
  ),
};

export function QrIcon() {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M14 14h3v3h-3zM20 14v.01M14 20h.01M17 20h4v-3" />
    </svg>
  );
}
